import { Heading, Modal, Paragraph, spacing, TextArea, useSnackbar } from 'component-library';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { isFromSweden } from '@sr/utils';
import { LocationPicker } from 'core/LocationPicker';
import { useLocations } from 'core/lib/Locations';
import { FormWrapper } from './styles';
import { useRNPSupportRequest } from './useRNPSupportRequest';

type ContactSupportProps = {
  showContactInfo: boolean;
  onClose: () => void;
  partnerId?: string;
  context: 'REQUEST_NEW_LOCATION' | 'ADD_CONTAINERS';
  performRnpFilter: boolean;
  onlySwedishLocations: boolean;
};

export const ContactSupport = ({
  showContactInfo,
  onClose,
  partnerId,
  context,
  performRnpFilter,
  onlySwedishLocations,
}: ContactSupportProps) => {
  const { t } = useTranslation();

  const [description, setDescription] = useState('');
  const [descriptionError, setDescriptionError] = useState(false);

  const [locationError, setLocationError] = useState(false);

  const { partnerTrees, loading: locationsLoading } = useLocations({
    filterServiceName: 'services.order.pickup.name',
  });

  const locations = useMemo(() => {
    if (locationsLoading) return [];

    return partnerTrees.filter((pt) => {
      let shouldInclude = true;

      if (onlySwedishLocations) {
        const isSwedish = isFromSweden(pt.item.dwKey);

        shouldInclude = isSwedish;
      }

      if (performRnpFilter) {
        shouldInclude = shouldInclude && !!pt.item.hasRnpAccess;
      }

      return shouldInclude;
    });
  }, [locationsLoading, onlySwedishLocations, partnerTrees, performRnpFilter]);

  const [selectedLocation, setSelectedLocation] = useState(
    partnerId || partnerTrees[0]?.item.dwKey,
  );

  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    if (partnerId) {
      setSelectedLocation(partnerId);
    }
  }, [partnerId, locations]);

  const resetForm = () => {
    setDescription('');
    setDescriptionError(false);
    setLocationError(false);
  };

  useEffect(() => {
    if (description) {
      setDescriptionError(false);
    }
  }, [description]);

  const { mutateAsync: submitSupportRequest, isPending: submitting } = useRNPSupportRequest();

  const onSubmit = async () => {
    if (!description) {
      setDescriptionError(true);

      return;
    }

    if (!selectedLocation) {
      setLocationError(true);

      return;
    }

    const result = await submitSupportRequest({
      description,
      partnerId: selectedLocation,
      origin: context,
    });

    if (!result) {
      showSnackbar({
        description: t('add.container.contact.submit.error'),
        severity: 'error',
      });
    } else {
      showSnackbar({
        description: t('add.container.contact.submit.success'),
        severity: 'success',
      });
    }

    // After successful submission
    onClose();
    resetForm();
  };

  return (
    <Modal
      open={showContactInfo}
      onClose={() => {
        resetForm();
        onClose();
      }}
      primaryAction={{
        label: t('add.container.contact.submit'),
        isLoading: submitting,
        action: () => {
          return onSubmit();
        },
      }}
      secondaryAction={{
        label: t('add.container.contact.cancel'),
        action: () => {
          resetForm();
          onClose();
        },
      }}
    >
      <Heading as="h1" variant="large" mb={spacing.xsmall}>
        {t('add.container.contact.heading')}
      </Heading>
      <Paragraph variant="large" mb="mediumLow">
        {t('add.container.contact.description')}
      </Paragraph>
      <FormWrapper>
        <TextArea
          defaultValue={description}
          onChange={(e) => {
            setDescription(e.target.value);
          }}
          label={t('add.container.contact.description.label')}
          characterLimit={500}
          required={true}
          errorMessage={descriptionError ? t('add.container.contact.description.error') : undefined}
        />
        <LocationPicker
          required={true}
          locations={locations}
          loading={locationsLoading}
          autoSelect="first"
          hideInactiveLocations={true}
          selectedValues={selectedLocation ? [selectedLocation] : []}
          multiSelect={false}
          onChange={(values) => {
            if (!values.length) return;

            const val = values[0];

            setSelectedLocation(val);
          }}
          description={t('add.container.contact.location.description')}
          errorMessage={t('add.container.contact.location.error')}
          error={locationError}
        />
      </FormWrapper>
    </Modal>
  );
};
